import type { NativeMessage, WebMessage } from '../protocol/protocol';
import { bridge } from './bridge';
import type { EventHandler, UnsubscribeFn } from './bridge';

/**
 * Типизированная обёртка над `bridge`-синглтоном.
 * Даёт автодополнение для конкретного набора действий/событий мини-аппа.
 * Системные действия (navigation.*, auth.*, ui.*) остаются в `bridge` напрямую —
 * их типизирует `SystemActionPayloads` / `SystemEventPayloads`.
 */
export interface TypedBridgeSdk<
  Actions extends WebMessage,
  Events extends NativeMessage
> {
  readonly isReady: boolean;
  /**
   * fire-and-forget: доменное действие web → native.
   * Payload сопоставляется с action через discriminated lookup — нельзя
   * передать payload одного действия с именем другого.
   */
  send<K extends Actions['action']>(
    action: K,
    payload: Extract<Actions, { action: K }>['payload']
  ): void;
  /** RPC: доменное действие с ожиданием ответа (редко нужно для домена) */
  request<K extends Actions['action'], R = unknown>(
    action: K,
    payload: Extract<Actions, { action: K }>['payload'],
    timeoutMs?: number
  ): Promise<R>;
  /** Подписаться на доменное событие native → web */
  on<K extends Events['event']>(
    event: K,
    handler: EventHandler<Extract<Events, { event: K }>['payload']>
  ): UnsubscribeFn;
}

/**
 * Создать типизированный bridge для конкретного мини-аппа.
 *
 * Типовые параметры:
 *   - `Actions` — объединение `WebMessage<action, payload>` допустимых действий
 *   - `Events`  — объединение `NativeMessage<event, payload>` ожидаемых событий
 *
 * @example
 * ```ts
 * type AppAction =
 *   | WebMessage<'order.submit', { orderId: string }>
 *   | WebMessage<'order.setStep', { step: number }>;
 * type AppEvent = NativeMessage<'order.statusChanged', { orderId: string; status: string }>;
 * const appBridge = createTypedBridge<AppAction, AppEvent>();
 *
 * appBridge.send('order.submit', { orderId: '42' });               // ✓
 * appBridge.send('order.submit', { step: 1 });                     // ✗ TS-ошибка: payload не от order.submit
 * appBridge.send('order.cancel', { orderId: '42' });               // ✗ TS-ошибка: нет такого действия
 * appBridge.on('order.statusChanged', ({ orderId, status }) => {}); // ✓
 * ```
 */
export function createTypedBridge<
  Actions extends WebMessage,
  Events extends NativeMessage
>(): TypedBridgeSdk<Actions, Events> {
  // Реализация делегирует незатипизированному bridge-синглтону.
  // Касты допустимы: тип-безопасность обеспечивается на стороне вызова через TypedBridgeSdk.
  return {
    get isReady() {
      return bridge.isReady;
    },
    send: bridge.send.bind(bridge) as TypedBridgeSdk<Actions, Events>['send'],
    request: bridge.request.bind(bridge) as TypedBridgeSdk<
      Actions,
      Events
    >['request'],
    on: bridge.on.bind(bridge) as TypedBridgeSdk<Actions, Events>['on']
  };
}
